import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { mtfLabel, tfSeconds } from '@/utils/mtf'

const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatTime(t, timeDisplay = 'local') {
  if (t == null) return '-'
  const seconds = t > 1e10 ? Math.floor(t / 1000) : t
  const date = new Date(seconds * 1000)
  if (Number.isNaN(date.getTime())) return '-'
  if (timeDisplay === 'utc') {
    return `${date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]}, ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`
  }
  return date.toLocaleString('es-ES', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function MitigationBlockQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0.0',
  onHeaderMouseDown,
  timeDisplay = 'local',
}) {
  const data = result || {}

  // Los contadores y eventos del badge solo miran el TF PROPIO del chart; las
  // zonas HTF (marcadas con `_mtf` por el merge MTF) van en su línea aparte.
  const activeMbs = (data.mbs || []).filter(mb => mb.is_active !== false && !mb._mtf)
  const bullMbs = activeMbs.filter(mb => {
    const d = String(mb.direction || '').toLowerCase()
    return d === 'bull' || d === 'bisi' || d === 'long' || d === 'buy'
  })
  const bearMbs = activeMbs.filter(mb => {
    const d = String(mb.direction || '').toLowerCase()
    return d === 'bear' || d === 'sibi' || d === 'short' || d === 'sell'
  })
  const mitigatedMbs = (data.mitigated_mbs || []).filter(mb => !mb._mtf)
  const invalidatedMbs = (data.invalidated_mbs || []).filter(mb => !mb._mtf)

  // Contadores 1:1 con el canvas: si el render expone lo pintado, manda eso.
  // (Las zonas devueltas se pintan TODAS; los únicos filtros son los toggles
  // del propio panel, que el usuario ve reflejados igual en badge y chart.)
  const painted = data._paintedCounts || null
  const countBull = painted ? painted.active_bull : bullMbs.length
  const countBear = painted ? painted.active_bear : bearMbs.length
  const countMitigated = painted ? painted.mitigated : mitigatedMbs.length
  const countInvalidated = painted ? painted.invalidated : invalidatedMbs.length

  const bias =
    countBull > countBear ? 'bull' :
    countBear > countBull ? 'bear' : 'neutral'
  const biasColor = bias === 'bull' ? '#089981' : bias === 'bear' ? '#f23645' : '#B08D57'

  const allZones = [...activeMbs, ...mitigatedMbs, ...invalidatedMbs].sort((a, b) => {
    const ta = (a.start_time > 1e10 ? Math.floor(a.start_time / 1000) : a.start_time) ?? 0
    const tb = (b.start_time > 1e10 ? Math.floor(b.start_time / 1000) : b.start_time) ?? 0
    return tb - ta
  })
  const lastZone = allZones[0]

  const lastMitigated = mitigatedMbs.slice().sort((a, b) => {
    const ta = (a.mitigated_at > 1e10 ? Math.floor(a.mitigated_at / 1000) : a.mitigated_at) ||
               (a.start_time > 1e10 ? Math.floor(a.start_time / 1000) : a.start_time) || 0
    const tb = (b.mitigated_at > 1e10 ? Math.floor(b.mitigated_at / 1000) : b.mitigated_at) ||
               (b.start_time > 1e10 ? Math.floor(b.start_time / 1000) : b.start_time) || 0
    return tb - ta
  })[0]

  // Línea MTF: solo si hay TFs adicionales activos (lo marca el merge con
  // `_mtfTimeframes`). Conteos de zonas HTF realmente pintadas, por TF.
  const mtfTfs = [...(data._mtfTimeframes || [])].sort((a, b) => (tfSeconds(a) ?? 0) - (tfSeconds(b) ?? 0))
  const htfLine = mtfTfs.map((tf) => `${mtfLabel(tf)} ×${painted?.htf?.[tf] ?? 0}`).join(' · ')

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          Mitigation Block Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: biasColor }}>· {bias.toUpperCase()}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        )}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Sesgo</span>
              <span className="text-[11px] font-semibold" style={{ color: biasColor }}>{bias.toUpperCase()}</span>
            </div>
          </div>

          <div className="flex flex-col gap-0.5 px-2.5 pb-1">
            <CountBar label="MB bull" value={countBull} color="#089981" />
            <CountBar label="MB bear" value={countBear} color="#f23645" />
            <CountBar label="Mitigados" value={countMitigated} color="#B08D57" />
            <CountBar label="Invalidados" value={countInvalidated} color="#64748b" />
          </div>

          {mtfTfs.length > 0 && (
            <div className="flex items-center justify-between px-2.5 pb-1">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>HTF</span>
              <span className="text-[10px] font-medium tabular-nums" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{htfLine}</span>
            </div>
          )}

          {lastZone && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1 flex items-center justify-between">
                <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Último formado</span>
                <span className="text-[10px] font-medium" style={{ color: '#B08D57' }}>
                  {lastZone.direction === 'bull' ? 'MB+' : 'MB-'} · {formatTime(lastZone.start_time, timeDisplay)}
                </span>
              </div>
            </div>
          )}

          <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1 flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Último mitigado</span>
              <span className="text-[10px] font-medium" style={{ color: '#64748b' }}>
                {lastMitigated
                  ? `${lastMitigated.direction === 'bull' ? 'MB+' : 'MB-'} · ${formatTime(lastMitigated.mitigated_at ?? lastMitigated.start_time, timeDisplay)}`
                  : '-'}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function CountBar({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>{label}</span>
      <span className="text-[10px] font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  )
}
